// Creative configuration constants — channel formats, per-channel form fields,
// and the metaDefaults() helper. Extracted from creative.tsx so library-picker
// can read them without creating a creative ↔ library-picker import cycle.

import {
  META_CREATIVE_CTA_ACCEPTED_LABELS,
  META_CREATIVE_CTA_REFUSALS,
  META_CREATIVE_CTA_VALUES,
  META_HEADLINE_MAX_CHARS,
  META_HEADLINE_RECOMMENDED_CHARS,
  META_LINK_DESCRIPTION_MAX_CHARS,
  META_LINK_DESCRIPTION_RECOMMENDED_CHARS,
  META_POST_TEXT_MAX_CHARS,
  META_POST_TEXT_RECOMMENDED_CHARS,
} from '@/api/types.gen';
import metaCatalog from '@shared/specs/meta-creative-v25.json';
import ctvCatalog from '@shared/specs/ttd-ctv-v1.json';
import { DISPLAY_MAX_BYTES, DISPLAY_SIZES, type DisplaySize } from '@/lib/media-validation/display';

/** The three creative channels. */
export type CreativeChannel = 'meta' | 'ctv' | 'dsp';

export function isCreativeChannel(value: unknown): value is CreativeChannel {
  return value === 'meta' || value === 'ctv' || value === 'dsp';
}

/** One Meta placement crop: its aspect ratio and the smallest asset that fills it. */
export interface CreativeFormat {
  key: string;
  name: string;
  spec: string;
  w: number;
  h: number;
  ratio: number;
  minW: number;
  minH: number;
}

/** What a channel accepts. `limits` is Meta's, `ctv` the CTV video spec, `dsp` the Display catalog. */
export interface ChannelConfig {
  name: string;
  blurb: string;
  formats: readonly CreativeFormat[];
  limits?: { image: number; video: number; ratioTol: number };
  ctv?: {
    ratio: number;
    ratioTol: number;
    minW: number;
    minH: number;
    durations: readonly number[];
    durTol: number;
    maxBytes: number;
  };
  dsp?: { sizes: DisplaySize[]; tol: number; maxBytes: number };
}

/** One form field of a channel's creative. `max` is the recommended display length (drives the
 * counter), `hardMax` what the API refuses. */
export interface CreativeField {
  key: string;
  label: string;
  type: 'input' | 'textarea' | 'select';
  help?: string;
  rows?: number;
  max?: number;
  hardMax?: number;
  mono?: boolean;
  options?: readonly string[];
}

/** A creative form's values, keyed by field. Every field is text. */
export type CreativeFieldValues = Record<string, string>;

/** A form problem anchored to the field it is about. */
export interface CreativeFieldIssue {
  key: string;
  message: string;
}

// Two different numbers on purpose. `max` is Meta's recommended display length
// and drives the character counter — past it the ad still runs, it just gets a
// "See more" clamp. `hardMax` is what the API actually rejects. Wiring an
// input's maxLength to `max` would block copy the API accepts, which is exactly
// how the generated defaults below became unsaveable.

// No channel carries a `defaults` list any more. CTV and Display used to declare
// one — "Inventory: Hulu, Roku, Samsung Ads", "Frequency cap 3/day",
// "Viewability ≥ 70%" — which the creative screen rendered as cards headed
// "Locked default" with a green check. Nothing in this product enforces any of
// them. Meta's equivalent had already been replaced by the real
// `GET /meta/delivery-contract`; these were the same boilerplate in a more
// confident font. A truthful CTV/TTD delivery contract is a server change.
export const CHANNEL_FORMATS = {
  meta: {
    name: 'Meta',
    blurb: 'Facebook + Instagram · feed, stories, reels',
    formats: [
      { key: 'sq',    name: 'Feed (1:1)',          spec: '1080×1080',   w: 1, h: 1,  ratio: 1.0,    minW: 1080, minH: 1080 },
      { key: 'fs',    name: 'Feed (4:5)',          spec: '1080×1350',   w: 4, h: 5,  ratio: 0.8,    minW: 1080, minH: 1350 },
      { key: 'story', name: 'Story / Reel (9:16)', spec: '1080×1920',   w: 9, h: 16, ratio: 0.5625, minW: 1080, minH: 1920 },
    ] satisfies CreativeFormat[],
    // The catalog's numbers, which `shared::meta_video` (video) and the server's
    // validation hold to: never re-typed here.
    limits: {
      image: metaCatalog.global.image.effective_max_bytes,
      video: metaCatalog.global.video.effective_max_bytes,
      ratioTol: 0.01,
    },
  },
  ctv: {
    name: 'CTV',
    blurb: 'Premium streaming platforms and FAST channels',
    formats: [] as CreativeFormat[],
    // From `shared/specs/ttd-ctv-v1.json`, the catalog `shared::ttd_ctv` (the server's
    // limits) is held to: never re-typed here.
    ctv: {
      ratio: ctvCatalog.aspect_ratio.width / ctvCatalog.aspect_ratio.height,
      ratioTol: ctvCatalog.aspect_tolerance,
      minW: ctvCatalog.min_width,
      minH: ctvCatalog.min_height,
      durations: ctvCatalog.durations_ms.map((ms) => ms / 1000),
      durTol: ctvCatalog.duration_tolerance_ms / 1000,
      maxBytes: ctvCatalog.max_bytes,
    },
  },
  dsp: {
    name: 'Display',
    blurb: 'Premium curated websites',
    formats: [] as CreativeFormat[],
    // From `shared/specs/ttd-display-v1.json`, the catalog the server enforces at
    // upload and attach: these were six sizes and 200 KiB typed here; SAM takes ten
    // sizes up to 250 KB, warning past TTD's recommended 150 KB (owner, 2026-10-08).
    dsp: {
      sizes: DISPLAY_SIZES.map((size) => ({ ...size })),
      tol: 0,
      maxBytes: DISPLAY_MAX_BYTES,
    },
  },
} satisfies Record<CreativeChannel, ChannelConfig>;

export const CHANNEL_FIELDS: Record<CreativeChannel, readonly CreativeField[]> = {
  meta: [
    { key: 'ad_name', label: 'Ad Name', type: 'input', help: 'Internal name — not shown to viewers' },
    { key: 'post_text', label: 'Post Text', type: 'textarea', rows: 3, help: 'Appears above the creative in feed', max: META_POST_TEXT_RECOMMENDED_CHARS, hardMax: META_POST_TEXT_MAX_CHARS },
    { key: 'headline', label: 'Headline', type: 'input', max: META_HEADLINE_RECOMMENDED_CHARS, hardMax: META_HEADLINE_MAX_CHARS },
    { key: 'link_desc', label: 'Link Description', type: 'input', max: META_LINK_DESCRIPTION_RECOMMENDED_CHARS, hardMax: META_LINK_DESCRIPTION_MAX_CHARS },
    { key: 'cta', label: 'CTA Button', type: 'select', options: [...META_CREATIVE_CTA_VALUES] },
    { key: 'display_url', label: 'Display link', type: 'input', mono: true, help: 'Rendered link shown on the ad' },
    { key: 'dest_url', label: 'Website URL', type: 'input', mono: true, help: 'Main destination; HTTPS website only' },
  ],
  ctv: [
    { key: 'ad_name', label: 'Ad Name', type: 'input', help: 'Internal name — not shown to viewers' },
    { key: 'dest_url', label: 'Destination URL', type: 'input', mono: true, help: 'Click-through (some CTVs surface a QR or remote prompt)' },
  ],
  dsp: [
    { key: 'ad_name', label: 'Ad Name', type: 'input', help: 'Internal name — not shown to viewers' },
    { key: 'dest_url', label: 'Destination URL', type: 'input', mono: true, help: 'Click-through (with UTM)' },
  ],
};

export const META_COPY_SPEC_KEYS = [
  'post_text',
  'headline',
  'link_desc',
  'cta',
  'display_url',
] as const;

export type MetaCopySpecKey = (typeof META_COPY_SPEC_KEYS)[number];

export function parseMetaCreativeSpec(spec: unknown): Partial<Record<MetaCopySpecKey, string>> {
  if (!spec || typeof spec !== 'string') return {};
  try {
    const decoded: unknown = JSON.parse(spec);
    if (!decoded || Array.isArray(decoded) || typeof decoded !== 'object') return {};
    const parsed: Partial<Record<MetaCopySpecKey, string>> = {};
    for (const key of META_COPY_SPEC_KEYS) {
      const value: unknown = Reflect.get(decoded, key);
      if (typeof value === 'string') parsed[key] = value;
    }
    return parsed;
  } catch {
    // Historical values such as "Multi-placement" remain readable and simply
    // fall back to the current presentation defaults.
    return {};
  }
}

export function serializeMetaCreativeSpec(values: Readonly<Record<string, unknown>> = {}): string {
  // Keep the literal property order stable: the string is used in campaign
  // fingerprints and must not change based on input edit order.
  return JSON.stringify({
    post_text: String(values.post_text ?? ''),
    headline: String(values.headline ?? ''),
    link_desc: String(values.link_desc ?? ''),
    cta: String(values.cta ?? ''),
    // No fallback. A blank display link is refused by `metaCopyIssues` and by
    // `shared::MetaCreativeSpec::validate`; quietly substituting telecharge.com
    // here would put back exactly what the operator had just cleared.
    display_url: String(values.display_url ?? '').trim(),
  });
}

/** The parts of a creative the copy helpers read; any creative row satisfies it. */
export interface CreativeCopySource {
  channel?: string | null;
  spec?: string | null;
  destination_url?: string | null;
}

export function metaFieldsFromCreative(
  creative: CreativeCopySource | null | undefined,
): Partial<Record<MetaCopySpecKey | 'dest_url', string>> {
  return {
    ...parseMetaCreativeSpec(creative?.spec),
    ...(typeof creative?.destination_url === 'string'
      ? { dest_url: creative.destination_url }
      : {}),
  };
}

export function creativeSpecDisplay(creative: CreativeCopySource | null | undefined): string {
  return creative?.channel === 'meta'
    && Object.keys(parseMetaCreativeSpec(creative?.spec)).length > 0
    ? 'Multi-placement'
    : (creative?.spec || '—');
}

export function isValidCreativeDestinationUrl(value: unknown): boolean {
  if (typeof value !== 'string' || value.trim() !== value || !value || value.length > 2048) {
    return false;
  }
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.replace(/\.$/, '').toLowerCase();
    return parsed.protocol === 'https:'
      && !parsed.username
      && !parsed.password
      && !!host
      && host !== 'invalid'
      && !host.endsWith('.invalid');
  } catch {
    return false;
  }
}

/** Mirror of `shared::validate_meta_display_url`.
 *
 *  Deliberately hand-parsed rather than delegating to `new URL()`. WHATWG
 *  normalises away exactly the things the server rejects — it punycodes IDN
 *  hosts, treats `\` as `/`, and tolerates trailing dots and underscores — so
 *  the two disagreed on a dozen inputs and the field went green on values the
 *  API then refused. Keep this in lockstep with the Rust function; the shared
 *  cases are pinned in creative-config.test.ts. */
export function isValidMetaDisplayUrl(value: unknown): boolean {
  if (typeof value !== 'string' || !value || value.trim() !== value) return false;
  // The server measures bytes; a multi-byte path could pass here and fail there.
  if (new TextEncoder().encode(value).length > 255) return false;
  if (value.includes('://') || value.includes('@') || value.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(value)) return false;

  const authorityEnd = value.search(/[/?#]/);
  const authority = authorityEnd === -1 ? value : value.slice(0, authorityEnd);
  const [host = '', ...portParts] = authority.split(':');
  if (portParts.length > 1) return false;
  if (portParts.length === 1 && !/^[0-9]+$/.test(portParts[0] ?? '')) return false;
  if (!host.includes('.') || host.startsWith('.')) return false;
  if (host.replace(/\.+$/, '').toLowerCase().endsWith('.invalid')) return false;
  if (!/^[A-Za-z0-9.-]+$/.test(host)) return false;
  return host
    .split('.')
    .every((label) => label.length > 0 && !label.startsWith('-') && !label.endsWith('-'));
}

/** Why a stored CTA label cannot be published, in the API's own words
 *  (`shared::meta_cta_publish_refusal`, generated as
 *  `META_CREATIVE_CTA_REFUSALS`); `undefined` for a label it has no sentence
 *  for. Own keys only — the record is a plain object literal, so a label such as
 *  `constructor` must not resolve to `Object.prototype`. */
export function metaCtaRefusal(label: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(META_CREATIVE_CTA_REFUSALS, label)
    ? META_CREATIVE_CTA_REFUSALS[label]
    : undefined;
}

/** Blocking problems in the Meta copy block, keyed by field so a save gate can
 *  anchor the message to the input instead of raising a page-level banner.
 *  Mirrors `shared::MetaCreativeSpec::validate` — required-ness and the CTA
 *  allowlist only. Length is NOT checked: the counter's number is Meta's
 *  display recommendation, not a limit.
 *
 *  A CTA that cannot publish is refused — the offered labels and the stored
 *  "Get Tickets" spelling of Buy Tickets both can — and a stored one Meta refuses
 *  (a "Book Now" revision from before 2026-10-02) says why instead of the
 *  generic prompt: the dropdown no longer lists it, so it renders as
 *  "— Select —" and the reason is the only place its label still appears. */
export function isAcceptedMetaCtaLabel(label: string): boolean {
  return META_CREATIVE_CTA_ACCEPTED_LABELS.some((accepted) => accepted === label);
}

export function metaCopyIssues(values: Readonly<Record<string, unknown>> = {}): CreativeFieldIssue[] {
  const issues: CreativeFieldIssue[] = [];
  const text = (key: string) => String(values[key] ?? '').trim();
  if (!text('post_text')) issues.push({ key: 'post_text', message: 'Post text is required.' });
  if (!text('headline')) issues.push({ key: 'headline', message: 'Headline is required.' });
  const cta = text('cta');
  if (!isAcceptedMetaCtaLabel(cta)) {
    issues.push({ key: 'cta', message: metaCtaRefusal(cta) ?? 'Choose a CTA button.' });
  }
  if (!isValidMetaDisplayUrl(text('display_url'))) {
    issues.push({ key: 'display_url', message: 'Enter a display link like tickets.example.com.' });
  }
  return issues;
}

/** The options a select field renders for `value`. A stored CTA that still
 *  publishes but is no longer offered — "Get Tickets", what every revision
 *  before 2026-10-02 carries, now offered as "Buy Tickets" — stays listed on the
 *  creative that carries it, so it is not shown as "— Select —". */
export function selectOptionsFor(field: CreativeField, value: unknown): string[] {
  const options: string[] = [...(field.options ?? [])];
  const current = String(value ?? '').trim();
  if (
    field.key === 'cta'
    && current
    && !options.includes(current)
    && isAcceptedMetaCtaLabel(current)
  ) {
    options.push(current);
  }
  return options;
}

/** `issues` without the refusal of a stored CTA that maps to a Meta type but is
 *  not one Graph accepts ("Book Now", "See Showtimes", "Sign Up").
 *
 *  That refusal is about publishing the copy, not about reading it: the spec
 *  parsed, its label is known, and a request that does not carry `spec` (a
 *  rename, a destination or agency edit) is one the API accepts. So a caller
 *  asking "is this pin readable?" or gating a PATCH without `spec` drops it, and
 *  every gate that does send or publish the copy keeps it. Any other CTA issue —
 *  a blank, an unknown label — stays. */
export function withoutStoredCtaRefusal<Issue extends { key: string }>(
  issues: Issue[],
  values: Readonly<Record<string, unknown>> = {},
): Issue[] {
  const cta = String(values.cta ?? '').trim();
  if (metaCtaRefusal(cta) === undefined) return issues;
  return issues.filter((issue) => issue.key !== 'cta');
}

/** The seed for a new creative's form. It carries NO copy: every field a viewer
 *  can read starts empty and the operator writes it.
 *
 *  It used to ship a full set of plausible strings — "Critics are calling it the
 *  must-see event of the season. Limited engagement at the {venue}.", a headline
 *  built from the title, and `https://shubert.com/{production_id}`. A default
 *  that looks finished gets treated as finished:
 *  `creative_55d72708cfd0442db67f14b7` reached Meta carrying every one of them
 *  verbatim, venue placeholder included — the id `f2d4c6b3-…` where a venue name
 *  belonged, and a destination that is not a ticketing URL. Nobody overwrote it;
 *  an operator simply never opened the copy form.
 *
 *  `creativeRequiredIssues` already refuses an empty post text, headline, CTA,
 *  display link and destination, so emptying these makes that gate fire on an
 *  untouched form instead of never firing.
 *
 *  The three values that remain are not copy. `ad_name` is an internal label
 *  built from what the operator already picked; `cta` is a Meta enum; and
 *  `display_url` is this business's one ticketing domain, which the server
 *  requires on every save. */
export function metaDefaults(
  production: { title: string } | null | undefined,
  _channel: string,
  cfgName: string,
  suffix?: string | null,
): CreativeFieldValues {
  return {
    ad_name: production ? `${production.title}${suffix?' · '+suffix:''} · ${cfgName}` : `Campaign${suffix?' · '+suffix:''} · ${cfgName}`,
    post_text: '',
    headline: '',
    link_desc: '',
    cta: 'Buy Tickets',
    display_url: 'telecharge.com',
    dest_url: '',
  };
}
